import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { modelingApi } from '@/api';
import { useModelingListQuery } from '@/hook';
import { VFlex, VInput, VSelect, VStack, VTextarea } from '@/package';
import { useModelTrainingFormContext } from '@/provider';
import type { Modeling } from '@/type';

const SelectModel = () => {
  const { t } = useTranslation();
  const {
    formState: { touchedFields },
    isModelNameError,
    clearErrors,
    register,
    setError,
    setValue,
    watch,
  } = useModelTrainingFormContext();
  const [modelName, setModelName] = useState<string | undefined>(undefined);
  const { data } = useModelingListQuery({
    versionType: 'Vueron',
  });

  useEffect(() => {
    if (!modelName) return;

    const timerId = setTimeout(async () => {
      const res = await modelingApi.getCount({
        name: modelName,
      });

      if (res.data.resultData === 0) {
        setValue('modelName', modelName);
      } else {
        setError('modelName', {
          type: 'duplicated',
          message: t(
            'modeling.modelNameIsExistsErrorMessage.denominationalist',
          ),
        });
      }
    }, 200);

    return () => clearTimeout(timerId);
  }, [modelName]);

  const formModelName = modelName ?? watch('modelName');

  return (
    <VStack gap={1} className="w-full">
      <VFlex gap={1}>
        <VSelect
          fluid
          label={t('modeling.selectModelLabel.osmograph')}
          required
          value={watch('sensorType')?.id ?? ''}
          onChange={e =>
            setValue(
              'sensorType',
              (data ?? []).find(m => m.id === e._target.value) as Modeling,
            )
          }
          placeholder={t('modeling.selectModelPlaceholder.derision')}
          itemList={
            data?.map(m => ({
              value: m.id,
              label: `${m.versionType === 'VersionModel' ? m.version : m.name} ${m.version ? `(v-${m.version})` : ''}`,
            })) ?? []
          }
        />

        <VInput
          fluid
          label={t('modeling.modelEpochLabel.bimane')}
          {...register('epoch')}
          onChange={e => {
            if (isNaN(Number(e.target.value))) {
              return;
            }
            setValue('epoch', Number(e.target.value));
          }}
          // readOnly
          onBlur={e => {
            const value = Number(e.target.value);

            if (isNaN(value) || e.target.value === '') {
              setValue('epoch', 4);
              return;
            }

            if (value < 4) {
              setValue('epoch', 4);
            } else if (value > 100) {
              setValue('epoch', 100);
            }
          }}
        />
      </VFlex>

      <VInput
        {...register('modelName')}
        label={t('modeling.modelNameLabel.demonist')}
        fluid
        required
        pattern="([A-Za-z0-9_\.\-])+"
        placeholder={t('modeling.modelNamePlaceholder.eulogism')}
        value={formModelName}
        onChange={e => {
          setModelName(e.target.value);

          if (!e.target.checkValidity()) {
            setError('modelName', {
              type: 'pattern',
              message: t('modeling.modelNameValidationErrorMessage.bargeman'),
            });
          } else {
            clearErrors('modelName');
          }
        }}
        valid={
          !touchedFields.modelName ||
          (touchedFields.modelName && !isModelNameError)
        }
        comment={isModelNameError ? isModelNameError.message : ''}
      />

      <VTextarea
        label={t('modeling.modelDescriptionLabel.badigeon')}
        placeholder={t('modeling.modelDescriptionPlaceholder.rhizocarpian')}
        value={watch('modelDescription')}
        onChange={e => setValue('modelDescription', e.target.value)}
        fluid
      />
    </VStack>
  );
};

export default SelectModel;
